import { useEffect, useRef, useState } from 'preact/hooks';
import { castAvailable, castConnected, castDevice, showCastPicker } from '../state/cast';
import {
  activeChapter,
  activeLiveChat,
  attachAudio,
  chapters,
  currentEpisode,
  durationSec,
  formatDuration,
  formatRemaining,
  formatTime,
  isBuffering,
  isLive,
  isMuted,
  isPlaying,
  jumpToChapter,
  liveChatOpen,
  liveItem,
  nowPlayingOpen,
  playbackError,
  playbackRate,
  playNext,
  positionSec,
  progressFraction,
  RATES,
  seekTo,
  setPlaybackRate,
  setSleepTimer,
  setVolume,
  skipBack,
  skipForward,
  sleepTimerAtEpisodeEnd,
  sleepTimerEndsAt,
  toggleMute,
  togglePlay,
  upNext,
  volume,
} from '../state/player';
import { sanitizeShowNotesHtml } from '../lib/sanitize';
import { settings } from '../state/session';
import { registerDiagnosticsAudio } from '../state/diagnostics';
import { Artwork } from './Artwork';
import { LiveChat } from './LiveChat';
import { StreamBadge } from './StreamBadge';
import { StreamInspectorModal } from './StreamInspectorModal';
import { TranscriptPanel } from './TranscriptPanel';
import {
  pushSample,
  rubberBand,
  shouldDismiss,
  velocityFrom,
  type Sample,
} from '../lib/gesture';
import { Icon } from './Icon';

// Elements a Tab trap should stop on while the sheet is open.
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Close functions for whichever of the sheet's popovers (speed, sleep timer) are currently open.
// The sheet's Escape handler consults this before closing itself, so Escape dismisses only the
// topmost layer (WAI-ARIA dialog pattern) instead of the popover and the sheet at once. A plain
// module-scope Set rather than a signal: nothing needs to re-render off it, the sheet's keydown
// handler just reads it at the moment Escape is pressed.
const openPopovers = new Set<() => void>();

/**
 * Registers `setOpen` in `openPopovers` for as long as `open` is true, so the sheet's Escape
 * handler can close this popover instead of itself. Shared by every popover in the sheet rather
 * than having the sheet reach into each one's local state.
 */
function useEscapeClosesPopover(open: boolean, setOpen: (open: boolean) => void) {
  useEffect(() => {
    if (!open) return;
    const closeThis = () => setOpen(false);
    openPopovers.add(closeThis);
    return () => {
      openPopovers.delete(closeThis);
    };
  }, [open, setOpen]);
}

/**
 * Mounted once at the app root, outside the router outlet, so route changes never unmount
 * the `<audio>` element and playback survives navigation.
 */
export function Player() {
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    if (audioRef.current) {
      attachAudio(audioRef.current);
      registerDiagnosticsAudio(audioRef.current);
    }
  }, []);

  const episode = currentEpisode.value;

  return (
    <>
      {/*
        No `crossorigin` attribute: setting it would require the podcast host to send CORS
        headers, and most do not — plain playback works everywhere without it. It would
        only be needed for Web Audio analysis, which this app does not do.
      */}
      <audio ref={audioRef} preload="metadata" />

      {episode && <MiniPlayer />}
      {episode && nowPlayingOpen.value && <NowPlayingSheet />}
      {isLive.value && liveChatOpen.value && <LiveChatDrawer />}
      <StreamInspectorModal />
    </>
  );
}

function MiniPlayer() {
  const episode = currentEpisode.value;
  if (!episode) return null;

  return (
    <div class="mini-player" role="region" aria-label="Now playing">
      <div class="mini-player__progress" aria-hidden="true">
        <div class="mini-player__progress-fill" style={{ width: `${progressFraction.value * 100}%` }} />
      </div>

      {/* Left Column: Artwork + Title + Feed/Chapter */}
      <button
        type="button"
        class="mini-player__expand"
        onClick={() => {
          nowPlayingOpen.value = true;
        }}
        aria-label={`Open now playing: ${episode.title}`}
      >
        <Artwork src={episode.imageUrl ?? episode.feedImageUrl} alt="" size={48} className="mini-player__art" />
        <span class="mini-player__meta">
          <span class="mini-player__title">{episode.title}</span>
          <span class="mini-player__show">
            {activeChapter.value?.title ?? episode.feedTitle ?? ''}
          </span>
        </span>
      </button>

      {/* Compact Play/Pause on mobile (< 900px) */}
      <button
        type="button"
        class="mini-player__mobile-play"
        onClick={togglePlay}
        aria-label={isPlaying.value ? 'Pause' : 'Play'}
      >
        <Icon name={isBuffering.value ? 'buffering' : isPlaying.value ? 'pause' : 'play'} size={20} />
      </button>

      {/* Center Column: Scrubber & Transport (Visible on Desktop) */}
      <div class="mini-player__center">
        <div class="mini-player__controls">
          <button
            type="button"
            class="icon-button"
            onClick={skipBack}
            aria-label={`Skip back ${settings.value.skipBackSec} seconds`}
          >
            <Icon name="replay" label={String(settings.value.skipBackSec)} />
          </button>
          <button
            type="button"
            class="icon-button icon-button--primary"
            onClick={togglePlay}
            aria-label={isPlaying.value ? 'Pause' : 'Play'}
          >
            <Icon name={isBuffering.value ? 'buffering' : isPlaying.value ? 'pause' : 'play'} />
          </button>
          <button
            type="button"
            class="icon-button"
            onClick={skipForward}
            aria-label={`Skip forward ${settings.value.skipForwardSec} seconds`}
          >
            <Icon name="forward" label={String(settings.value.skipForwardSec)} />
          </button>
        </div>
        <div class="mini-player__scrubber-wrapper">
          <Scrubber />
        </div>
      </div>

      {/* Right Column: Speed, Sleep, Cast, Volume & Expand (Desktop) */}
      <div class="mini-player__extra">
        {(isLive.value || activeLiveChat.value) && (
          <button
            type="button"
            class={`chip ${liveChatOpen.value ? 'is-active' : ''}`}
            onClick={() => {
              liveChatOpen.value = !liveChatOpen.value;
            }}
            aria-expanded={liveChatOpen.value}
            aria-label="Toggle live chat"
          >
            <Icon name="chat" size={18} />
            Live Chat
          </button>
        )}
        <SpeedControl />
        <SleepTimerControl />
        <CastControl />
        <StreamBadge />
        <VolumeControl />
        <button
          type="button"
          class="icon-button mini-player__expand-btn"
          onClick={() => {
            nowPlayingOpen.value = true;
          }}
          aria-label="Expand player details"
        >
          <Icon name="maximize" size={20} />
        </button>
      </div>
    </div>
  );
}

function VolumeControl() {
  const currentVol = volume.value;
  const muted = isMuted.value;
  const effectiveVol = muted ? 0 : currentVol;

  const iconName = muted || effectiveVol === 0 ? 'volume-mute' : effectiveVol < 0.5 ? 'volume-low' : 'volume';

  const volPct = Math.round(effectiveVol * 100);

  return (
    <div class="volume-control" role="group" aria-label="Volume">
      <button
        type="button"
        class="icon-button volume-control__btn"
        onClick={toggleMute}
        aria-label={muted ? 'Unmute' : 'Mute'}
      >
        <Icon name={iconName} size={20} />
      </button>
      <input
        type="range"
        class="volume-control__slider"
        min="0"
        max="1"
        step="0.01"
        value={effectiveVol}
        aria-label="Volume level"
        aria-valuetext={`${volPct} percent`}
        style={{ '--volume-pct': `${volPct}%` }}
        onInput={(e) => {
          const val = Number((e.target as HTMLInputElement).value);
          setVolume(val);
        }}
      />
    </div>
  );
}

type Tab = 'chapters' | 'transcript' | 'queue' | 'notes';

function NowPlayingSheet() {
  const episode = currentEpisode.value;
  const [tab, setTab] = useState<Tab>('chapters');
  const sheetRef = useRef<HTMLDivElement>(null);
  const dragStartY = useRef<number | null>(null);
  const samples = useRef<Sample[]>([]);
  const [dragOffset, setDragOffset] = useState(0);
  // While a finger is down the sheet must follow it exactly; the spring-back transition is
  // only wanted on release, so the class that carries it is toggled rather than always on.
  const [dragging, setDragging] = useState(false);

  const close = () => {
    nowPlayingOpen.value = false;
  };

  // Escape closes the sheet, matching every other modal surface on the platform — but only when
  // the sheet is the topmost layer. If a popover (speed, sleep timer) is open over it, Escape
  // must dismiss that popover first and leave the sheet in place, matching the WAI-ARIA dialog
  // pattern of dismissing one layer at a time.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // The sheet is a modal dialog and fully owns Escape while it's open, so this is the one
      // place that gets to act on it — stopImmediatePropagation guards against acting twice on
      // the same keypress (e.g. duplicate handlers momentarily present during a remount).
      e.stopImmediatePropagation();
      if (openPopovers.size > 0) {
        for (const closePopover of openPopovers) closePopover();
        openPopovers.clear();
        return;
      }
      close();
    };
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, []);

  // Move focus into the sheet so keyboard and screen-reader users land in the new context, trap
  // Tab within it while it's open (standard dialog behaviour), and — on close — hand focus back
  // to whatever triggered the sheet, so a keyboard user's tab position isn't reset to the top of
  // the document. The trigger is captured here rather than in a click handler because the sheet
  // can also be opened programmatically, and this effect's cleanup runs exactly once, on unmount,
  // which is also when the sheet closes (see the render in `Player`).
  useEffect(() => {
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    sheetRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const sheet = sheetRef.current;
      if (!sheet) return;
      const focusable = [...sheet.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)];
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const outside = !sheet.contains(active);
      const atEdge = e.shiftKey ? active === first || outside : active === last || outside;
      if (!atEdge) return;
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
    };
    addEventListener('keydown', onKeyDown);

    return () => {
      removeEventListener('keydown', onKeyDown);
      if (trigger && document.contains(trigger)) {
        trigger.focus();
      } else {
        // The trigger vanished (e.g. the mini-player unmounted underneath us) — fall back to the
        // mini-player's expand button rather than stranding focus on <body>.
        document.querySelector<HTMLElement>('.mini-player__expand')?.focus();
      }
    };
  }, []);

  if (!episode) return null;

  const available: Tab[] = ['notes'];
  if (chapters.value.length > 0) available.unshift('chapters');
  if (episode.transcripts?.length > 0) available.push('transcript');
  if (upNext.value.length > 0) available.push('queue');
  const activeTab = available.includes(tab) ? tab : available[0];

  /*
   * Drag-to-dismiss, with the two bits of physics that make it feel like a sheet rather than
   * a div: upward travel rubber-bands instead of tracking the finger, and the release
   * decision weighs how fast the finger was moving, not only how far it got. A quick flick
   * dismisses; a slow drag that stalls halfway springs back. See `lib/gesture`.
   */
  const onPointerDown = (e: PointerEvent) => {
    // Only block dragging on interactive controls and deep scrollable prose
    if ((e.target as Element).closest('button, input, a, select, textarea, .chapter-list, .upnext, .prose')) return;
    // A secondary button or a second finger is not a dismissal gesture.
    if (!e.isPrimary || e.button !== 0) return;
    /*
     * Capture the pointer, so the drag keeps receiving moves once it leaves the sheet's box.
     * Touch has implicit capture and was always fine; a mouse dragging *downwards* — the one
     * direction that dismisses — exits the bottom edge, and without capture the `pointerup`
     * would land elsewhere, leaving the sheet stranded mid-gesture with the drag still open.
     */
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    dragStartY.current = e.clientY;
    samples.current = [{ y: e.clientY, t: e.timeStamp }];
    setDragging(true);
  };
  const onPointerMove = (e: PointerEvent) => {
    if (dragStartY.current === null) return;
    pushSample(samples.current, { y: e.clientY, t: e.timeStamp });
    setDragOffset(rubberBand(e.clientY - dragStartY.current));
  };
  const onPointerUp = () => {
    if (dragStartY.current !== null && shouldDismiss(dragOffset, velocityFrom(samples.current))) {
      close();
    }
    dragStartY.current = null;
    samples.current = [];
    setDragging(false);
    setDragOffset(0);
  };

  return (
    <div class="np" role="dialog" aria-modal="true" aria-label={`Now playing: ${episode.title}`}>
      <div
        ref={sheetRef}
        class={`np__sheet ${dragging ? 'is-dragging' : ''}`}
        tabIndex={-1}
        style={dragOffset !== 0 ? { transform: `translateY(${dragOffset}px)` } : undefined}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <div class="np__grabber-zone" aria-hidden="true">
          <div class="np__grabber" />
        </div>
        <button type="button" class="np__close icon-button" onClick={close} aria-label="Close now playing">
          <Icon name="chevron-down" />
        </button>

        <div class="np__scroll">
          <Artwork
            src={activeChapter.value?.img ?? episode.imageUrl ?? episode.feedImageUrl}
            alt=""
            size={280}
            className="np__art"
          />

          <h2 class="np__title">{episode.title}</h2>
          <a class="np__show" href={`/podcast/${episode.feedId}`} onClick={close}>
            {episode.feedTitle}
          </a>

          {activeChapter.value?.title && (
            <p class="np__chapter" aria-live="polite">
              {activeChapter.value.title}
            </p>
          )}

          {castConnected.value && (
            <p class="np__cast" aria-live="polite">
              Playing on {castDevice.value ?? 'a nearby device'}
            </p>
          )}

          {playbackError.value && (
            <p class="np__error" role="alert">
              {playbackError.value}
            </p>
          )}

          <Scrubber />

          <div class="np__controls">
            <button
              type="button"
              class="icon-button"
              onClick={skipBack}
              aria-label={`Skip back ${settings.value.skipBackSec} seconds`}
            >
              <Icon name="replay" label={String(settings.value.skipBackSec)} size={30} />
            </button>
            <button
              type="button"
              class="icon-button icon-button--primary icon-button--large"
              onClick={togglePlay}
              aria-label={isPlaying.value ? 'Pause' : 'Play'}
            >
              <Icon name={isBuffering.value ? 'buffering' : isPlaying.value ? 'pause' : 'play'} size={34} />
            </button>
            <button
              type="button"
              class="icon-button"
              onClick={skipForward}
              aria-label={`Skip forward ${settings.value.skipForwardSec} seconds`}
            >
              <Icon name="forward" label={String(settings.value.skipForwardSec)} size={30} />
            </button>
          </div>

          <div class="np__secondary">
            {(isLive.value || activeLiveChat.value) && (
              <button
                type="button"
                class={`chip ${liveChatOpen.value ? 'is-active' : ''}`}
                onClick={() => {
                  liveChatOpen.value = !liveChatOpen.value;
                }}
                aria-expanded={liveChatOpen.value}
                aria-label="Toggle live chat"
              >
                <Icon name="chat" size={18} />
                Live Chat
              </button>
            )}
            <CastControl />
            <SpeedControl />
            <SleepTimerControl />
            <StreamBadge />
            {upNext.value.length > 0 && (
              <button type="button" class="chip" onClick={playNext}>
                Next up: {upNext.value[0].title.slice(0, 28)}…
              </button>
            )}
          </div>

          <div class="np__tabs" role="tablist" aria-label="Episode details">
            {available.map((name) => (
              <button
                key={name}
                type="button"
                role="tab"
                aria-selected={activeTab === name}
                class={`np__tab ${activeTab === name ? 'is-active' : ''}`}
                onClick={() => setTab(name)}
              >
                {name === 'notes' ? 'Show notes' : name[0].toUpperCase() + name.slice(1)}
              </button>
            ))}
          </div>

          <div class="np__panel" role="tabpanel">
            {activeTab === 'chapters' && <ChapterList />}
            {activeTab === 'transcript' && <TranscriptPanel episodeId={episode.id} />}
            {activeTab === 'queue' && <UpNextList />}
            {activeTab === 'notes' && (
              <div
                class="prose"
                // Show notes are publisher HTML, sanitized to a safe subset — see
                // src/client/lib/sanitize.ts.
                dangerouslySetInnerHTML={{
                  __html: sanitizeShowNotesHtml(episode.description ?? '<p>No show notes.</p>'),
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Scrubber() {
  /*
   * Scrubbing is the hottest interaction in the app — it fires at pointer rate over a subtree
   * that includes the chapter ticks — and the naive shape, one `setState` per `input` event,
   * re-renders all of it a hundred-odd times a second. (The sheet drag above also sets state
   * per pointer event, but it writes a single transform onto one element, so the diff is
   * cheap enough not to be worth the same treatment.)
   *
   * Instead the drag is taken out of Preact's hands entirely:
   *   - `scrubbing` flips once on drag start and once on release, so this component renders
   *     twice per gesture rather than hundreds (in the sheet, that is the whole story; the
   *     mini-player's copy is re-rendered by its parent on every `timeupdate` anyway, and
   *     still avoids the per-event work below);
   *   - while it is set, the component never reads `positionSec.value`, so it holds no
   *     subscription to it and playback's own updates cannot re-render it (nor fight the
   *     finger by resetting the input's value mid-drag);
   *   - the timestamps that must keep up are written straight to the DOM inside a single
   *     `requestAnimationFrame`, coalescing a burst of pointer events into one paint.
   *
   * The input's own thumb position needs no help — the browser moves it natively.
   */
  const [scrubbing, setScrubbing] = useState(false);
  const pending = useRef(0);
  const frame = useRef<number | null>(null);
  const rangeRef = useRef<HTMLInputElement>(null);
  const elapsedRef = useRef<HTMLSpanElement>(null);
  const remainingRef = useRef<HTMLSpanElement>(null);

  const duration = durationSec.value;
  // Conditional read, deliberately: see above.
  const shown = scrubbing ? pending.current : positionSec.value;

  // A frame still queued when the drag ends (or the player unmounts) would write stale text
  // over the settled value.
  const cancelFrame = () => {
    if (frame.current !== null) {
      cancelAnimationFrame(frame.current);
      frame.current = null;
    }
  };
  useEffect(() => cancelFrame, []);

  /*
   * Writes go to the existing text node's `nodeValue`, never to the span's `textContent`.
   * Assigning `textContent` discards the current child and inserts a *new* text node, while
   * Preact's vdom keeps pointing at the old, now-detached one — so every later render would
   * update a node that is no longer in the document and these timestamps would stay frozen
   * at whatever the last scrub wrote, for the rest of the session. Mutating in place keeps
   * the node Preact owns, so the handover back to normal rendering is seamless.
   */
  const paint = () => {
    frame.current = null;
    const at = pending.current;
    const elapsed = elapsedRef.current?.firstChild;
    if (elapsed) elapsed.nodeValue = formatTime(at);
    const remaining = remainingRef.current?.firstChild;
    if (remaining && duration) remaining.nodeValue = formatRemaining(at, duration);
    if (rangeRef.current && duration > 0) {
      const pct = Math.min(100, Math.max(0, (at / duration) * 100));
      rangeRef.current.style.setProperty('--progress-pct', `${pct}%`);
    }
  };

  const onInput = (e: Event) => {
    pending.current = Number((e.target as HTMLInputElement).value);
    if (!scrubbing) setScrubbing(true);
    if (frame.current === null) frame.current = requestAnimationFrame(paint);
  };

  const commit = (e: Event) => {
    cancelFrame();
    const at = Number((e.target as HTMLInputElement).value);
    pending.current = at;
    seekTo(at);
    setScrubbing(false);
  };

  // A chapter at 0:00 would sit under the thumb's own start; only later ones need a tick.
  // `toc: false` marks a chapter the publisher wants kept out of listings, ticks included.
  const marks = duration
    ? chapters.value.filter((c) => c.startTime > 0 && c.toc !== false && c.startTime < duration)
    : [];

  if (isLive.value) {
    const item = liveItem.value;
    const startTime = item?.startTime ? item.startTime * 1000 : Date.now();
    const elapsedMs = Math.max(0, Date.now() - startTime);

    return (
      <div class="scrubber scrubber--live">
        <div class="live-player-status">
          <span class="live-indicator-badge live-pulse">🔴 LIVE</span>
          <span class="live-elapsed-time">Elapsed: {formatDuration(elapsedMs)}</span>
        </div>
      </div>
    );
  }

  const progressPct = duration > 0 ? `${Math.min(100, Math.max(0, (shown / duration) * 100))}%` : '0%';

  return (
    <div class="scrubber">
      <div class="scrubber__track">
        <input
          ref={rangeRef}
          class="scrubber__range"
          type="range"
          min={0}
          max={duration || 0}
          step={1}
          value={shown}
          disabled={!duration}
          aria-label="Seek"
          aria-valuetext={`${formatTime(shown)} of ${formatTime(duration)}`}
          style={{ '--progress-pct': progressPct }}
          onInput={onInput}
          onChange={commit}
        />
        <div class="scrubber__ticks" aria-hidden="true">
          {marks.map((chapter) => (
            <span
              key={chapter.startTime}
              class="scrubber__tick"
              // Positioned by the stylesheet off this custom property, so the inline style
              // stays direction-agnostic and the RTL flip lives with the rest of the CSS.
              style={{ '--tick-at': `${(chapter.startTime / duration) * 100}%` }}
              title={chapter.title ?? formatTime(chapter.startTime)}
            />
          ))}
        </div>
      </div>
      <div class="scrubber__times">
        <span ref={elapsedRef}>{formatTime(shown)}</span>
        <span ref={remainingRef}>{duration ? formatRemaining(shown, duration) : '--:--'}</span>
      </div>
    </div>
  );
}

/**
 * Cast, offered only when there is something to cast to.
 *
 * `castAvailable` is false in every browser and on any phone with no receiver on the network, so
 * this renders nothing at all rather than a button that opens an empty list — the behaviour the
 * Cast UX guidelines ask for. The picker itself is the platform's, drawn natively by the shell.
 */
function CastControl() {
  if (!castAvailable.value) return null;

  const connected = castConnected.value;
  const device = castDevice.value;

  return (
    <button
      type="button"
      class={`chip ${connected ? 'is-active' : ''}`}
      onClick={() => void showCastPicker()}
      aria-label={
        connected
          ? `Casting to ${device ?? 'a nearby device'}. Choose another device or stop casting.`
          : 'Cast to a device'
      }
    >
      <Icon name={connected ? 'cast-connected' : 'cast'} size={18} />
      {connected ? (device ?? 'Casting') : 'Cast'}
    </button>
  );
}

function SpeedControl() {
  const [open, setOpen] = useState(false);
  useEscapeClosesPopover(open, setOpen);
  return (
    <div class="popover-host">
      <button
        type="button"
        class="chip"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={`Playback speed, currently ${playbackRate.value} times`}
      >
        {playbackRate.value}×
      </button>
      {open && (
        <div class="popover" role="menu">
          {RATES.map((rate) => (
            <button
              key={rate}
              type="button"
              role="menuitemradio"
              aria-checked={playbackRate.value === rate}
              class={`popover__item ${playbackRate.value === rate ? 'is-active' : ''}`}
              onClick={() => {
                setPlaybackRate(rate);
                setOpen(false);
              }}
            >
              {rate}×
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SleepTimerControl() {
  const [open, setOpen] = useState(false);
  const endsAt = sleepTimerEndsAt.value;
  const active = endsAt !== null || sleepTimerAtEpisodeEnd.value;

  const label = sleepTimerAtEpisodeEnd.value
    ? 'End of episode'
    : endsAt
      ? `${Math.max(0, Math.round((endsAt - Date.now()) / 60000))} min`
      : 'Sleep';

  useEscapeClosesPopover(open, setOpen);

  return (
    <div class="popover-host">
      <button
        type="button"
        class={`chip ${active ? 'is-active' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={`Sleep timer, currently ${active ? label : 'off'}`}
      >
        {label}
      </button>
      {open && (
        <div class="popover" role="menu">
          {[5, 10, 15, 30, 45, 60].map((min) => (
            <button
              key={min}
              type="button"
              role="menuitem"
              class="popover__item"
              onClick={() => {
                setSleepTimer(min);
                setOpen(false);
              }}
            >
              {min} minutes
            </button>
          ))}
          <button
            type="button"
            role="menuitem"
            class="popover__item"
            onClick={() => {
              setSleepTimer(null, true);
              setOpen(false);
            }}
          >
            End of episode
          </button>
          {active && (
            <button
              type="button"
              role="menuitem"
              class="popover__item"
              onClick={() => {
                setSleepTimer(null);
                setOpen(false);
              }}
            >
              Turn off
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function LiveChatDrawer() {
  const item = liveItem.value || activeLiveChat.value;
  const drawerRef = useRef<HTMLDivElement>(null);

  const close = () => {
    liveChatOpen.value = false;
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopImmediatePropagation();
      close();
    };
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    drawerRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const drawer = drawerRef.current;
      if (!drawer) return;
      const focusable = [...drawer.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)];
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const outside = !drawer.contains(active);
      const atEdge = e.shiftKey ? active === first || outside : active === last || outside;
      if (!atEdge) return;
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
    };
    addEventListener('keydown', onKeyDown);

    return () => {
      removeEventListener('keydown', onKeyDown);
      if (trigger && document.contains(trigger)) {
        trigger.focus();
      }
    };
  }, []);

  if (!item) return null;

  return (
    <div
      class="live-chat-drawer-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Live episode chat drawer"
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        ref={drawerRef}
        class="live-chat-drawer"
        tabIndex={-1}
      >
        <button
          type="button"
          class="live-chat-drawer__close icon-button"
          onClick={close}
          aria-label="Close live chat"
        >
          <Icon name="x" size={20} />
        </button>
        <div class="live-chat-drawer__content">
          <LiveChat feedId={item.feedId} guid={item.guid} onClose={close} />
        </div>
      </div>
    </div>
  );
}


/**
 * The ultrawide side deck.
 *
 * Past about 1440px the centred content column stops growing and the space either side is
 * simply empty. Rather than stretch the reading measure past comfort, that space becomes a
 * persistent rail carrying what a listener otherwise has to open a modal sheet to see:
 * chapters, the transcript, and what is up next.
 *
 * The sheet is not replaced — on a laptop or a phone it is still the way in, and the deck is
 * hidden by the stylesheet below the breakpoint. This renders nothing at all when there is
 * no episode, or when the episode has none of the three things worth showing, so the layout
 * collapses back to a single column instead of reserving a rail full of empty state.
 */
export function NowPlayingDeck() {
  const episode = currentEpisode.value;
  const [tab, setTab] = useState<Tab>('chapters');
  if (!episode) return null;

  const available: Tab[] = [];
  if (chapters.value.length > 0) available.push('chapters');
  if (episode.transcripts.length > 0) available.push('transcript');
  if (upNext.value.length > 0) available.push('queue');
  if (available.length === 0) return null;

  const activeTab = available.includes(tab) ? tab : available[0];

  return (
    <aside class="deck" aria-label="Playback details">
      <div class="deck__header">
        <Artwork
          src={activeChapter.value?.img ?? episode.imageUrl ?? episode.feedImageUrl}
          alt=""
          size={72}
          className="deck__art"
        />
        <div class="deck__meta">
          <p class="deck__title">{episode.title}</p>
          <a class="deck__show muted" href={`/podcast/${episode.feedId}`}>
            {episode.feedTitle}
          </a>
        </div>
      </div>

      <div class="np__tabs" role="tablist" aria-label="Episode details">
        {available.map((name) => (
          <button
            key={name}
            type="button"
            role="tab"
            aria-selected={activeTab === name}
            class={`np__tab ${activeTab === name ? 'is-active' : ''}`}
            onClick={() => setTab(name)}
          >
            {name[0].toUpperCase() + name.slice(1)}
          </button>
        ))}
      </div>

      <div class="deck__panel" role="tabpanel">
        {activeTab === 'chapters' && <ChapterList />}
        {activeTab === 'transcript' && <TranscriptPanel episodeId={episode.id} />}
        {activeTab === 'queue' && <UpNextList />}
      </div>
    </aside>
  );
}

function ChapterList() {
  const list = chapters.value;
  const active = activeChapter.value;
  if (list.length === 0) return <p class="muted">This episode has no chapters.</p>;

  return (
    <ol class="chapter-list">
      {list.map((chapter) => (
        <li key={chapter.startTime}>
          <button
            type="button"
            class={`chapter-list__item ${active?.startTime === chapter.startTime ? 'is-active' : ''}`}
            onClick={() => jumpToChapter(chapter)}
            aria-current={active?.startTime === chapter.startTime}
          >
            <span class="chapter-list__time">{formatTime(chapter.startTime)}</span>
            <span class="chapter-list__title">{chapter.title ?? 'Untitled chapter'}</span>
          </button>
        </li>
      ))}
    </ol>
  );
}

function UpNextList() {
  return (
    <ol class="upnext">
      {upNext.value.map((episode) => (
        <li key={episode.id} class="upnext__item">
          <Artwork src={episode.imageUrl ?? episode.feedImageUrl} alt="" size={40} />
          <span class="upnext__meta">
            <span class="upnext__title">{episode.title}</span>
            <span class="muted">{episode.feedTitle}</span>
          </span>
        </li>
      ))}
    </ol>
  );
}
